import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
type MaxWidthToken = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
const MAX_WIDTH_CLASS: Record<MaxWidthToken, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl'
};
interface CenteredModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Tailwind max-width token for the inner panel. Default 'md'. */
  maxWidth?: MaxWidthToken;
  /** Extra classes on the inner panel (e.g. background, padding, height). */
  panelClassName?: string;
  /** Tailwind classes on the backdrop. Default: bg-slate-900/40 backdrop-blur-sm. */
  backdropClassName?: string;
  /** Disable closing on backdrop click (e.g. for commit-flows). */
  disableBackdropClose?: boolean;
  /** Optional spring tuning for panel enter/exit. */
  springBounce?: number;
  springDuration?: number;
  children: React.ReactNode;
}
/**
 * CenteredModal — Shared modal shell for the Learning Lab.
 *
 * Owns:
 * - AnimatePresence + fixed-inset positioning
 * - Backdrop fade + click-to-close
 * - Panel scale/y enter-exit
 * - Max-width sizing
 *
 * Consumers provide the panel content (header, body, actions).
 * Part of the Learning Lab v1.4 Wave C structural refactor (F-UI-7).
 */
export function CenteredModal({
  isOpen,
  onClose,
  maxWidth = 'md',
  panelClassName = 'bg-white rounded-3xl shadow-2xl overflow-hidden',
  backdropClassName = 'absolute inset-0 bg-slate-900/40 backdrop-blur-sm',
  disableBackdropClose = false,
  springBounce,
  springDuration,
  children
}: CenteredModalProps) {
  const widthClass = MAX_WIDTH_CLASS[maxWidth];
  return <AnimatePresence data-id="element-2964">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-2965">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className={backdropClassName} onClick={disableBackdropClose ? undefined : onClose} data-id="element-2966" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={springBounce !== undefined || springDuration !== undefined ? {
        type: 'spring',
        bounce: springBounce ?? 0.3,
        duration: springDuration ?? 0.45
      } : undefined} className={`relative w-full ${widthClass} ${panelClassName}`} data-id="element-2967">
            {children}
          </motion.div>
        </div>}
    </AnimatePresence>;
}